import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import jwtService from "@/common/jwt.service";
import { getAPIUrl } from "@/common/envMixins";
import { isNullOrUndefined } from "util";

Vue.use(Vuex);

//isLoaded fields are 0,1,2. 0 for false, 1 for true and 2 for error
export const baseUrl = getAPIUrl();
export const authUrl = `${baseUrl}/Auth?returnUrl=${window.location.href}`;

axios.interceptors.response.use(response => {
    return response;
    }, error => {
        //if api calls fail after page has been sitting for a while (user was authenticated for page but then changed term or something), redirect to auth
        if (error.response.status === 401 || error.response.status == 403) {
            window.location.replace(authUrl);
        } else {
            Promise.reject(error);
        }
    });


export default new Vuex.Store({
    state: {
        profileInfoIsLoaded: 0,
        gradesAreLoaded: 0,
        coursesAreLoaded: 0,
        announcementsAreLoaded: 0,
        balanceIsLoaded: 0,
        imageIsLoaded: 0,
        userInfo: {
            "displayName" : "",
            "isAuthenticated" : false,
            "isImpersonating" : false
        },
        profileImage: "images/placeholder.jpg",
        studentProfileInfo: {},
        studentGrades: {},
        studentCourses: {},
        balanceAndHolds: {},
        announcements: {},
        message: null,
        documentToUpload: null,
        docIsUploaded: 0 

    },
    mutations: {
        setUserInfo (state, val) {
            state.userInfo = val;
        },
        setProfileImage(state, val) {
            state.profileImage = val;
        },
        setStudentProfileInfo (state, val) {
            state.studentProfileInfo = val;
            state.profileInfoIsLoaded = 1;
        },
        setGrades (state, val) {
            state.studentGrades = val;
            window.$cookies.set("grades_SelectedTerm", val.defaultTermCode);
            state.gradesAreLoaded = 1;
        },
        setCourses (state, val) {
            state.studentCourses = val;
            window.$cookies.set("courses_SelectedTerm", val.defaultTermCode);
            state.coursesAreLoaded = 1;
        },
        setBalanceAndHolds (state, val) {
            state.balanceAndHolds = val;
            state.balanceIsLoaded = 1;
        },
        setAnnouncements (state, val) {
            state.announcements = val;
            state.announcementsAreLoaded = 1;
        },
        setGradesLoaded (state, val) {
            state.gradesAreLoaded = val;
        },
        setAnnouncementsLoaded (state, val) {
            state.announcementsAreLoaded = val;
        },
        setCoursesLoaded (state, val) {
            state.coursesAreLoaded = val;
        },
        setProfileLoaded (state, val) {
            state.profileInfoIsLoaded = val;
        },
        setBalanceLoaded (state, val) {
            state.balanceIsLoaded = val;
        },
        setImageLoaded(state, val) {
            state.imageIsLoaded = val;
        },
        setMessage(state, val) {
            state.message = val;
        },
        setDocumentToUpload(state, val) {
            state.documentToUpload = val;
        },
        setDocIsUploaded(state, val) {
            state.docIsUploaded = val;
        }
    },
    //we want to get this information from rest calls
    actions: {
        getProfileImage(context) {
            context.commit("setImageLoaded", 0);

            axios.get(baseUrl + "/api/ProfileImage")
                .then(r => {
                    context.commit("setProfileImage", `data:image/jpg;base64,${r.data}`);
                })
                .catch(() => context.commit("setImageLoaded", 2));
        },
        getStudentProfileInfo (context) {
            context.commit("setProfileLoaded", 0);
       
            axios.get(baseUrl + "/api/StudentProfile")
                .then(r => context.commit("setStudentProfileInfo", r.data))
                .catch(() => context.commit("setProfileLoaded", 2));     
        },
        getGrades (context) {
            context.commit("setGradesLoaded", 0);
            
            axios.get(baseUrl + "/api/StudentGrades")
                .then(r => { 
                    const defaultTermCookie = window.$cookies.get("grades_SelectedTerm");
                    let gradesJson = r.data;
                    if (!isNullOrUndefined(defaultTermCookie)) {
                        gradesJson.defaultTermCode = defaultTermCookie; 
                    } else {
                        window.$cookies.set("grades_SelectedTerm", gradesJson.defaultTermCode);
                    }
                    context.commit("setGrades", r.data);
                    if (context.state.studentGrades == null || context.state.studentGrades.grades == null) {
                        context.commit("setGradesLoaded", 2);
                    }
                })
                .catch(() => context.commit("setGradesLoaded", 2));
           
        },
        getCourses (context) {
            context.commit("setCoursesLoaded", 0);
            const defaultTermCookie = window.$cookies.get("courses_SelectedTerm");
            let courseUrl = baseUrl + "/api/StudentCourses";
            if (!isNullOrUndefined(defaultTermCookie)) {
                courseUrl += "/" + defaultTermCookie;
            }
            axios.get(courseUrl)
                .then(r => {
                    //we want to set the default term code to a term code that the user has previously set
                    let courseJson = r.data;
                    if (!isNullOrUndefined(defaultTermCookie)) {
                        if (courseJson.termList.filter(x=> x.value == defaultTermCookie).length > 0) {
                            courseJson.defaultTermCode = defaultTermCookie;
                        }        
                    } else {
                        window.$cookies.set("courses_SelectedTerm", courseJson.defaultTermCode);
                    }
                    context.commit("setCourses", courseJson);
                })
                .catch(() => {
                    context.commit("setCoursesLoaded", 2)
                });
  
        },
        getBalanceAndHolds (context) {
            context.commit("setBalanceLoaded", 0);
            
            axios.get(baseUrl + "/api/StudentBalance")
                .then(r => {
                    context.commit("setBalanceAndHolds", r.data)
                })
                .catch(() => {
                    context.commit("setBalanceLoaded", 2)
                });

        },
        getAnnouncements (context) {
            context.commit("setAnnouncementsLoaded", 0);
            
            axios.get(baseUrl + "/api/Announcements")
                .then(r => context.commit("setAnnouncements", r.data))
                .catch(() => context.commit("setAnnouncementsLoaded", 2));
             
        },
        getMessage(context) {
            axios.get(baseUrl + "/api/Outage")
                .then(r => context.commit("setMessage", r.data))
                .catch();
        },
        uploadDocument(context) {
            var formData = new FormData();
            formData.append("DocFile", context.state.documentToUpload);
            axios.post(baseUrl + "/api/DocumentUpload",
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }).then(() => {
                    context.commit("setDocIsUploaded", 2);
                    context.commit("setDocumentToUpload", null);
                }).catch(() => context.commit("setDocIsUploaded", 3));
        },
        checkAuth(context) {
            //get jwt and set auth fields from it.
            try {
                let rawjwt = jwtService.getToken();
                let jwt = rawjwt ? JSON.parse(atob(rawjwt.split('.')[1])) : null;
                let isAuthenticated = jwt ? jwt.exp < (new Date().getTime() / 1000) ? false : true : false;

                //if the token has less than 12 hours left, renew it
                if (isAuthenticated && jwt.exp < ((new Date().getTime() / 1000) + 43200)) {
                    axios.get(baseUrl + "/auth/renew?token=" + rawjwt)
                        .then(r => jwtService.createToken(r.data));
                }

                if (!isAuthenticated) {
                    window.location.replace(authUrl);
                }

                let authobject = {
                    "displayName": isAuthenticated ? jwt["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"] : "",
                    "isAuthenticated": isAuthenticated,
                    "isImpersonating": isAuthenticated ? jwt.isImpersonating : false
                };

                if (isAuthenticated) {
                    jwtService.setAuthHeader();
                }

                context.commit("setUserInfo", authobject);
            } catch {
                window.location.replace(authUrl);
            }
            
        }


    }
});